import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Box, Dialog, DialogContent, Button, Typography, TextField, Card, Chip, useTheme, IconButton } from '@mui/material';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import dayjs from 'dayjs';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import { authenticationService } from 'src/services/authentication.service';
import { get } from 'src/services/httpClient.service';
import AddPayment from '../OTT-Payments/AddPayments';
import AddOnlinePayment from '../OTT-Payments/AddOnlinePayment';
import AddOnlineFranchisePayment from './AddOnlineFranchisePayment';
import AddFranchisePayment from './AddFranchisePayment';
import DatePicker from '@mui/lab/DatePicker';
import { getResponsiveStyles, isMobileScreen } from 'src/common/utils/responsive';
import SearchIcon from '@mui/icons-material/Search';
import DateRangeIcon from '@mui/icons-material/DateRange';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';

const OTTFranchisePayments = () => {
  const theme = useTheme();
  const styles = getResponsiveStyles(theme);
  const [isMobile, setIsMobile] = useState(isMobileScreen(theme));

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileScreen(theme));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [theme]);

  const [tableData, setTableData] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openOnlinePaymentDialog, setOpenOnlinePaymentDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);
  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  const handleOnlinePaymentOpenDialog = () => setOpenOnlinePaymentDialog(true);
  const handleOnlinePaymentCloseDialog = () => setOpenOnlinePaymentDialog(false);

  /* filter states for paydate */
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // Mobile filter states
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showMobileDateFilters, setShowMobileDateFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCards, setExpandedCards] = useState({});
  const [visibleCount, setVisibleCount] = useState(10);

  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();

  const API_URL = process.env.REACT_APP_BASE_API_URL;

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await get(`${API_URL}/api/franchise-payments/vendor/${vendorId}/franchise/${franchiseId}/findAll`);
      const formattedData = response.map((row, index) => ({
        ...row,
        id: row.id ?? row.paymentId ?? index
      }));
      setTableData(formattedData);
    } catch (error) {
      console.error('Error fetching payment data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter by date range
  const getDateFilteredData = () => {
    return tableData.filter((row) => {
      const paymentDate = dayjs(row.paymentDate);

      if (fromDate && toDate) {
        return (
          paymentDate.isAfter(dayjs(fromDate).startOf('day')) &&
          paymentDate.isBefore(dayjs(toDate).endOf('day'))
        );
      }

      if (fromDate) {
        return paymentDate.isAfter(dayjs(fromDate).startOf('day'));
      }

      if (toDate) {
        return paymentDate.isBefore(dayjs(toDate).endOf('day'));
      }

      return true;
    });
  };

  // Filter by search query
  const getSearchFilteredData = (data) => {
    if (!searchQuery) return data;

    return data.filter((row) =>
      Object.values(row).some(
        (value) => value && value.toString().toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  };

  const filteredData = useMemo(() => {
    const dateFiltered = getDateFilteredData();
    return getSearchFilteredData(dateFiltered);
  }, [tableData, fromDate, toDate, searchQuery]);

  const visibleRows = filteredData.slice(0, visibleCount);

  useEffect(() => {
    fetchData();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS':
        return '#2e7d32';
      case 'CREATED':
      case 'PENDING':
        return '#ed6c02';
      case 'FAILED':
      case 'CANCELLED':
        return '#d32f2f';
      case 'REFUNDED':
        return '#ed6c02';
      default:
        return '#757575';
    }
  };

  const columns = [
    // { field: 'id', headerName: 'ID', width: 80 },
    { field: 'paymentId', headerName: 'Payment ID', width: 150 },
    { field: 'franchiseName', headerName: 'Franchise', width: 250 },
    { field: 'amount', headerName: 'Amount', width: 100 },
    // { field: 'currency', headerName: 'Currency', width: 100 },
    { field: 'transactionType', headerName: 'Transaction Type', width: 180 },
    { field: 'paymentType', headerName: 'Payment Type', width: 160 },
    // { field: 'transactionId', headerName: 'Transaction ID', width: 180 },
    // { field: 'paymentGateway', headerName: 'Gateway', width: 150 },
    {
      field: 'paymentDate',
      headerName: 'Payment Date',
      width: 180,
      valueFormatter: (params) => params.value ? dayjs(params.value).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    { 
      field: 'paymentStatus', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => {
        const status = params.value;
        return <span style={{ color: getStatusColor(status), fontWeight: 'bold' }}>{status}</span>;
      }
    },
    // { field: 'refundId', headerName: 'Refund ID', width: 150 },
    // { field: 'refundAmount', headerName: 'Refund Amount', width: 120 },
    {
      field: 'createdDate',
      headerName: 'Created',
      width: 180,
      valueFormatter: (params) => params.value ? dayjs(params.value).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    // {
    //   field: 'updatedDate',
    //   headerName: 'Updated',
    //   width: 180,
    //   valueFormatter: (params) => params.value ? dayjs(params.value).format('YYYY-MM-DD HH:mm:ss') : '-'
    // },
  ];

  const handleExportCSV = () => {
    const headers = ['ID', 'Payment ID', 'Franchise ID', 'Amount', 'Method', 'Mode', 'Transaction ID', 'Status', 'Gateway', 'Payment Date', 'Refund ID', 'Refund Amount', 'Currency', 'Created Date', 'Updated Date'];
    const csvData = filteredData.map(row => [
      row.id,
      row.paymentId,
      row.franchiseId,
      row.amount || 0,
      row.paymentMethod,
      row.paymentMode,
      row.transactionId,
      row.paymentStatus,
      row.paymentGateway,
      row.paymentDate ? dayjs(row.paymentDate).format('YYYY-MM-DD HH:mm:ss') : '-',
      row.refundId || '-',
      row.refundAmount || 0,
      row.currency,
      row.createdDate ? dayjs(row.createdDate).format('YYYY-MM-DD HH:mm:ss') : '-',
      row.updatedDate ? dayjs(row.updatedDate).format('YYYY-MM-DD HH:mm:ss') : '-'
    ]);

    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `franchise_payments_${dayjs().format('YYYY-MM-DD')}.csv`;
    link.click();
  };

  const renderPaymentCard = (row) => {
    const isExpanded = expandedCards[row.id];

    return (
      <Card key={row.id} sx={styles.mobileCard}>
        <Box sx={styles.mobileCardHeader}>
          <Box>
            <Typography sx={styles.mobileCardTitle}>
              Franchise ID: {row.franchiseId}
            </Typography>
            <Typography sx={styles.mobileCardSubText}>
              Payment ID: {row.paymentId}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>
            ₹{row.amount || 0}
          </Typography>
          <Chip
            label={row.paymentStatus}
            size="small"
            sx={{
              backgroundColor: getStatusColor(row.paymentStatus),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        </Box>

        <Typography sx={{ ...styles.mobileCardText, mt: 1 }}>
          📝 Method: {row.paymentMethod}
        </Typography>

        <Typography sx={{ ...styles.mobileCardText, mt: 0.5 }}>
          🔄 Mode: {row.paymentMode}
        </Typography>

        <Typography sx={{ ...styles.mobileCardText, mt: 0.5 }}>
          🏦 Gateway: {row.paymentGateway || '-'}
        </Typography>

        <Typography sx={{ ...styles.mobileCardText, mt: 0.5 }}>
          💳 Transaction ID: {row.transactionId}
        </Typography>

        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            onClick={() =>
              setExpandedCards((prev) => ({
                ...prev,
                [row.id]: !prev[row.id]
              }))
            }
          >
            {isExpanded ? 'Hide Details' : 'View More'}
          </Button>
        </Box>

        {isExpanded && (
          <Box sx={{ mt: 1.5, fontSize: '13px', opacity: 0.9 }}>
            <Typography>Payment Date: {row.paymentDate ? dayjs(row.paymentDate).format('YYYY-MM-DD HH:mm:ss') : '-'}</Typography>
            <Typography>Created: {row.createdDate ? dayjs(row.createdDate).format('YYYY-MM-DD HH:mm:ss') : '-'}</Typography>
            <Typography>Updated: {row.updatedDate ? dayjs(row.updatedDate).format('YYYY-MM-DD HH:mm:ss') : '-'}</Typography>
            {row.refundId && (
              <>
                <Typography>Refund ID: {row.refundId}</Typography>
                <Typography>Refund Amount: ₹{row.refundAmount || 0}</Typography>
              </>
            )}
          </Box>
        )}
      </Card>
    );
  };

  return (
    <>
      <Helmet>
        <title>Franchise Payments</title>
      </Helmet>

      {/* 📱 MOBILE HEADER - Only visible on mobile */}
      {isMobile && (
        <>
          <Box sx={styles.mobileHeader}>
            <Typography sx={styles.mobileTitle}>
              Franchise Payment List
            </Typography>

            <Box sx={styles.mobileHeaderActions}>
              {hasPermission(["OWNER"]) && (
                <Button
                  variant="contained"
                  onClick={handleOpenDialog}
                  sx={styles.mobileAddButton}
                >
                  Add
                </Button>
              )}
              {hasPermission(["OWNER", "DISTRIBUTOR"]) && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleOnlinePaymentOpenDialog}
                  sx={styles.mobileAddButton}
                >
                  Online
                </Button>
              )}
            </Box>
          </Box>

          {/* 📱 MOBILE FILTER BAR */}
          <Box sx={styles.mobileFilterBar}>
            <Button
              variant="contained"
              onClick={() => setShowMobileSearch(prev => !prev)}
              sx={{
                ...styles.mobileIconButton,
                backgroundColor: showMobileSearch ? '#1976d2' : '#90caf9',
                '&:hover': { backgroundColor: '#115293' }
              }}
            >
              <SearchIcon fontSize="small" />
            </Button>

            <Button
              variant="contained"
              onClick={() => setShowMobileDateFilters(prev => !prev)}
              sx={{
                ...styles.mobileIconButton,
                backgroundColor: showMobileDateFilters ? '#ed6c02' : '#ffcc80',
                '&:hover': { backgroundColor: '#e65100' }
              }}
            >
              <DateRangeIcon fontSize="small" />
            </Button>

            <Button
              onClick={handleExportCSV}
              sx={{
                ...styles.mobileSmallIconButton,
                backgroundColor: '#2e7d32',
                '&:hover': { backgroundColor: '#1b5e20' }
              }}
            >
              <DownloadIcon fontSize="small" />
            </Button>

            <Button
              onClick={fetchData}
              sx={{
                ...styles.mobileSmallIconButton,
                backgroundColor: '#1976d2',
                '&:hover': { backgroundColor: '#115293' }
              }}
            >
              <RefreshIcon fontSize="small" />
            </Button>
          </Box>

          {/* 📱 MOBILE FILTER INPUTS */}
          {(showMobileSearch || showMobileDateFilters) && (
            <Box sx={{ margin: "20px", marginTop: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {showMobileSearch && (
                  <TextField
                    label="Search"
                    variant="outlined"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="Search by payment ID, franchise ID, transaction ID..."
                  />
                )}

                {showMobileDateFilters && (
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                    <DatePicker
                      label="From Date"
                      value={fromDate}
                      onChange={(newValue) => setFromDate(newValue)}
                      renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    />
                    <DatePicker
                      label="To Date"
                      value={toDate}
                      onChange={(newValue) => setToDate(newValue)}
                      renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setFromDate(null);
                        setToDate(null);
                      }}
                    >
                      Clear Filters
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </>
      )}

      {/* 💻 DESKTOP HEADER - Only visible on desktop/tablet */}
      {!isMobile && (
        <PageTitleWrapper>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Box>
              <PageTitle
                heading="Franchise Payment List"
                subHeading="Manage franchise payments"
                handleClick={undefined}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {hasPermission(["OWNER"]) && (
                <Button variant="contained" onClick={handleOpenDialog}>
                  Make Offline Payment
                </Button>
              )}
              {hasPermission(["OWNER", "DISTRIBUTOR"]) && (
                <Button variant="contained" color="secondary" onClick={handleOnlinePaymentOpenDialog}>
                  Make Online Payment
                </Button>
              )}
              <Button variant="outlined" onClick={handleExportCSV} startIcon={<DownloadIcon />}>
                Export CSV
              </Button>
            </Box>
          </Box>
        </PageTitleWrapper>
      )}

      {/* DIALOGS */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogContent>
          <AddFranchisePayment handleCloseDialog={handleCloseDialog} fetchData={fetchData} />
        </DialogContent>
      </Dialog>

      <Dialog open={openOnlinePaymentDialog} onClose={handleOnlinePaymentCloseDialog} maxWidth="sm" fullWidth>
        <DialogContent>
          <AddOnlineFranchisePayment handleCloseDialog={handleOnlinePaymentCloseDialog} fetchData={fetchData} />
        </DialogContent>
      </Dialog>

      {/* DESKTOP FILTERS SECTION */}
      {!isMobile && (
        <Box sx={{ margin: "20px" }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
              renderInput={(params) => <TextField {...params} size="small" />}
            />
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={(newValue) => setToDate(newValue)}
              renderInput={(params) => <TextField {...params} size="small" />}
            />
            <TextField
              label="Search"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              placeholder="Search payments..."
              sx={{ minWidth: 200 }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                setFromDate(null);
                setToDate(null);
                setSearchQuery('');
              }}
            >
              Clear Filters
            </Button>
          </Box>
        </Box>
      )}

      {/* MAIN CONTENT */}
      <Box sx={{ height: 'calc(100vh - 220px)', width: '100%', padding: '15px' }}>
        {isMobile ? (
          // 📱 MOBILE VIEW - Card Layout
          <Box sx={styles.mobileCardContainer}>
            {loading ? (
              <Typography sx={{ textAlign: 'center', py: 4 }}>Loading payments...</Typography>
            ) : visibleRows.length === 0 ? (
              <Typography sx={{ textAlign: 'center', py: 4 }}>No payments found</Typography>
            ) : (
              <>
                {visibleRows.map(renderPaymentCard)}

                {visibleCount < filteredData.length && (
                  <Box sx={styles.mobileLoadMore}>
                    <Button
                      variant="contained"
                      onClick={() => setVisibleCount((prev) => prev + 10)}
                    >
                      Load More ({visibleRows.length}/{filteredData.length})
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        ) : (
          // 💻 DESKTOP VIEW - DataGrid
          <DataGrid
            rows={filteredData}
            columns={columns}
            loading={loading}
            components={{ Toolbar: GridToolbar }}
            getRowId={(row) => row.id}
            sx={{
              boxShadow: 2,
              border: 2,
              borderColor: 'rgb(43 44 45)',
              '& .MuiDataGrid-cell:hover': { color: 'rgb(43 44 45)' }
            }}
            autoHeight={false}
            pagination
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[10, 25, 50]}
            paginationMode="client"
            disableSelectionOnClick
          />
        )}
      </Box>
    </>
  );
};

export default OTTFranchisePayments;